import Handsontable from 'handsontable/base';
import type { UndoEntry } from 'hyperformula/typings/UndoRedo';
import { isObject } from 'lodash';

type HyperFormulaSheetReferenceKey = (typeof HYPER_FORMULA_SHEET_ID_KEYS)[number];

type HyperFormulaSheetReferencePrimitive = string | number | boolean | null | undefined;

type HyperFormulaSheetReferenceObject = {
    readonly [key: string]: HyperFormulaSheetReferenceSearchValue;
};

export type HyperFormulaSheetReferenceSearchValue =
    | UndoEntry
    | HyperFormulaSheetReferencePrimitive
    | HyperFormulaSheetReferenceObject
    | readonly HyperFormulaSheetReferenceSearchValue[];

export type HyperFormulaUndoRedoStacks = {
    undoStack: UndoEntry[];
    redoStack: UndoEntry[];
};

type HyperFormulaCrudOperationsWithUndoRedoStacks = {
    undoRedo?: HyperFormulaUndoRedoStacks;
};

const HYPER_FORMULA_SHEET_ID_KEYS = ['sheetId', 'sheet', 'scope'] as const;

const isHyperFormulaSheetReferenceKey = (key: string): key is HyperFormulaSheetReferenceKey =>
    HYPER_FORMULA_SHEET_ID_KEYS.includes(key as HyperFormulaSheetReferenceKey);

/**
 * Replaces a HyperFormula history stack in place so the private stack object remains stable.
 */
const replaceStackContents = (baseStack: UndoEntry[], incomingStack: UndoEntry[]) => {
    baseStack.splice(0, baseStack.length, ...incomingStack);
};

/**
 * Returns HyperFormula's private undo/redo stacks so custom HOT actions can avoid polluting them.
 */
const getHyperFormulaUndoRedoStacks = (hotInstance: Handsontable): HyperFormulaUndoRedoStacks | undefined => {
    const formulasPlugin = hotInstance.getPlugin<'formulas'>('formulas');

    // @ts-ignore Access of private member
    const crudOperations = formulasPlugin?.engine?._crudOperations as
        | HyperFormulaCrudOperationsWithUndoRedoStacks
        | undefined;

    return crudOperations?.undoRedo;
};

/**
 * Finds whether a HyperFormula stack entry references the provided sheet.
 */
export const hasHyperFormulaSheetReference = (
    value: HyperFormulaSheetReferenceSearchValue,
    sheetId: number,
    visitedValues = new WeakSet<object>(),
): boolean => {
    if (!isObject(value)) return false;
    if (visitedValues.has(value)) return false;

    visitedValues.add(value);

    const entries = Object.entries(value as HyperFormulaSheetReferenceObject);

    return entries.some(([key, entryValue]) => {
        if (isHyperFormulaSheetReferenceKey(key) && entryValue === sheetId) return true;

        return hasHyperFormulaSheetReference(entryValue, sheetId, visitedValues);
    });
};

/**
 * Captures HyperFormula history before custom Milanote actions mutate sheet content outside native HOT undo/redo.
 */
export const captureHyperFormulaUndoRedoStack = (hotInstance: Handsontable): HyperFormulaUndoRedoStacks | null => {
    const undoRedoStacks = getHyperFormulaUndoRedoStacks(hotInstance);
    if (!undoRedoStacks) return null;

    return {
        undoStack: [...undoRedoStacks.undoStack],
        redoStack: [...undoRedoStacks.redoStack],
    };
};

/**
 * Restores HyperFormula history while keeping the current sheet data written by Milanote's snapshot replay.
 */
export const restoreHyperFormulaUndoRedoStack = (
    hotInstance: Handsontable,
    snapshot: HyperFormulaUndoRedoStacks | null,
): void => {
    if (!snapshot) return;

    const undoRedoStacks = getHyperFormulaUndoRedoStacks(hotInstance);
    if (!undoRedoStacks) return;

    replaceStackContents(undoRedoStacks.undoStack, snapshot.undoStack);
    replaceStackContents(undoRedoStacks.redoStack, snapshot.redoStack);
};

/**
 * Removes HyperFormula history entries for a sheet while preserving unrelated table history.
 */
export const removeHyperFormulaUndoRedoEntriesForSheet = (hotInstance: Handsontable, sheetId: number): void => {
    const undoRedoStacks = getHyperFormulaUndoRedoStacks(hotInstance);
    if (!undoRedoStacks) return;

    const filterStackEntry = (entry: UndoEntry) => !hasHyperFormulaSheetReference(entry, sheetId);

    replaceStackContents(undoRedoStacks.undoStack, undoRedoStacks.undoStack.filter(filterStackEntry));
    replaceStackContents(undoRedoStacks.redoStack, undoRedoStacks.redoStack.filter(filterStackEntry));
};

/**
 * Runs HyperFormula housekeeping while preserving the user-facing undo/redo history.
 */
export const runWithoutChangingHyperFormulaUndoRedoStack = (hotInstance: Handsontable, callback: () => void): void => {
    const snapshot = captureHyperFormulaUndoRedoStack(hotInstance);

    try {
        callback();
    } finally {
        restoreHyperFormulaUndoRedoStack(hotInstance, snapshot);
    }
};
